import { useEffect, useState } from 'react';
import { RARITY_COLOR, STACKS, ZONES, type Rarity, type StackKind, type StackMap, type ZoneId } from '@lastday/shared';
import { t } from '@/i18n';

const DAY_S = 86_400;

// ---- Server time (krug 3, U3) ----------------------------------------------------------
// Every timer runs on the server's clock: the difference to this device is measured on
// each /api/me and added to Date.now(), so a phone whose clock is off still counts right.
let serverOffset = 0;

/** Records the server time from a response (half the round trip is taken as travel time). */
export function syncServerTime(serverTime: number, sentAt: number, receivedAt: number) {
  const trip = receivedAt - sentAt;
  if (!Number.isFinite(serverTime) || trip < 0 || trip > 10_000) return;
  serverOffset = serverTime - (sentAt + receivedAt) / 2;
}

/** Now on the server's clock (ms). Use this instead of Date.now() for anything the server times. */
export function serverNow(): number {
  return Date.now() + serverOffset;
}

/** Server clock minus device clock, in ms. */
export function serverClockOffset(): number {
  return serverOffset;
}

/** "12 survivors · 30 militia": real players and generated fighters apart (plan v0.6, A3). */
export function hordeCrowd(h: { fighters: number; players: number }): string {
  const people = h.players === 1 ? t('horde.crowd.survivor') : t('horde.crowd.survivors', { n: num(h.players) });
  const militia = Math.max(0, h.fighters - h.players);
  return militia ? `${people} · ${t('horde.crowd.militia', { n: num(militia) })}` : people;
}

/** 725 s -> "12:05", 13 000 s -> "3:36:40"; a day or more -> "3d 7h" (C6). */
/**
 * Time left until something: "45s", "14m 25s", "1h 04m", days beyond. Krug 5 (K4.15): never
 * "14:25", which reads like a time on the clock (that is `hhmm`).
 */
export function clock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total >= DAY_S) return daysLabel(total);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

function daysLabel(totalSeconds: number): string {
  const d = Math.floor(totalSeconds / DAY_S);
  const h = Math.floor((totalSeconds % DAY_S) / 3600);
  return h ? `${d}d ${h}h` : `${d}d`;
}

/** Time left in words: "3d 7h", "6h 12m", "12m", "<1m". */
export function durationLabel(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total >= DAY_S) return daysLabel(total);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h) return m ? `${h}h ${m}m` : `${h}h`;
  return m ? `${m}m` : '<1m';
}

/** 20 -> "20 min", 90 -> "1h 30m", 240 -> "4h" */
export function minutesLabel(min: number): string {
  // Round the whole first: 1319.8 minutes is "22h", not "21h 60m" (26. 9. 2026).
  const total = Math.round(min);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h}h ${String(m).padStart(2, '0')}m` : `${h}h`;
}

export function num(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export function pct(p: number, digits = 0): string {
  return `${(p * 100).toFixed(digits)}%`;
}

export function timeAgo(ms: number): string {
  const s = Math.max(0, Math.round((serverNow() - ms) / 1000));
  if (s < 60) return t('common.ago.now');
  const m = Math.round(s / 60);
  if (m < 60) return t('common.ago.min', { n: m });
  const h = Math.round(m / 60);
  if (h < 24) return t('common.ago.h', { n: h });
  return t('common.ago.d', { n: Math.round(h / 24) });
}

export function hhmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export const rarityColor = (r: Rarity) => RARITY_COLOR[r];
/**
 * Rarity colour for small text (Crimson, 1. 10. 2026): Rare and Epic are lifted a little where the
 * original is under 4.5:1 on a card or a raised surface; frames, chips and icons keep `rarityColor`.
 */
export const rarityTextColor = (r: Rarity) => (r === 'rare' ? 'var(--color-rare-text)' : r === 'epic' ? 'var(--color-epic-text)' : RARITY_COLOR[r]);
export const rarityName = (r: Rarity) => t(`common.rarity.${r}`);
export const zoneName = (z: ZoneId | string) => ZONES[z as ZoneId]?.name ?? z;
export const stackName = (k: StackKind, qty = 1) => (qty === 1 ? STACKS[k]?.name : STACKS[k]?.plural ?? STACKS[k]?.name) ?? k;

/** The stacks with a count; Scrap first (K9b: the currency leads every price and every loot list). */
export function stacksList(m: StackMap | undefined): [StackKind, number][] {
  if (!m) return [];
  return (Object.entries(m) as [StackKind, number][]).filter(([, v]) => v > 0).sort(([a], [b]) => Number(b === 'scrap') - Number(a === 'scrap'));
}

/** What dismantling gives, "+12 Scrap · +2 Cloth · +1 Relic Shard" (krug 5, K7.2); Relic Shards last. */
export function gainText(scrap: number, extra?: StackMap): string {
  const parts = scrap > 0 ? [`+${num(scrap)} ${stackName('scrap', scrap)}`] : [];
  const list = stacksList(extra).filter(([k]) => k !== 'scrap').sort(([a], [b]) => Number(a === 'relic_shard') - Number(b === 'relic_shard'));
  for (const [k, v] of list) parts.push(`+${num(v)} ${stackName(k, v)}`);
  return parts.join(' · ');
}

/** Re-renders every `ms` so timers tick. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    const id = setInterval(() => setNow(serverNow()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/**
 * 23. 10. 2026 (owner, a player's feedback): a prompt that pops up over the screen (a run's event, an encounter) takes no
 * choice in its first `PROMPT_ARM_MS`, so a click meant for the screen under it does not pick an option unread.
 */
export const PROMPT_ARM_MS = 1000;

/** False until `ms` have passed since the component appeared. */
export function useArmed(ms = PROMPT_ARM_MS): boolean {
  const [armed, setArmed] = useState(ms <= 0);
  useEffect(() => {
    if (ms <= 0) return;
    const id = setTimeout(() => setArmed(true), ms);
    return () => clearTimeout(id);
  }, [ms]);
  return armed;
}

/** Milliseconds left until `target` (ticks every second). */
export function useCountdown(target: number | null | undefined): number {
  const now = useNow(1000);
  return target ? Math.max(0, target - now) : 0;
}

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}
